/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * Root Reducer
 * combine all reducers to create root reducer
 */

import { combineReducers, type Action } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import Storage from 'redux-persist/lib/storage';

import { employeesReducer } from '@app/modules/employees';
import { bootstrapReducer } from '@app/modules/bootstrap';
import { authReducer, logout } from '@app/modules/auth';
import { commonReducer, commonSlice } from '@app/modules/common';
import { departmentsReducer } from '@app/modules/departments';
import { designationsReducer } from '@app/modules/designation';
import { roleReducer } from '@app/modules/role';
import { myProfileReducer } from '@app/modules/my-profile';
import { clientsReducer } from '@app/modules/clients';
import { assetsReducer } from '@app/modules/assets';
import { holidaysReducer } from '@app/modules/holidays';
import { leaveManagementReducer } from '@app/modules/leave-setting';
import { leavesReducer } from '@app/modules/add-leave-employee';
import { candidateReducer } from '@app/modules/candidates';
import { documentsReducer } from '@app/modules/documents';
import { IntervieweeReducer } from '@app/modules/interviewee';
import { internsReducer } from '@app/modules/intern';
import { projectReducer } from '@app/modules/projects';
import { portfolioReducer } from '@app/modules/portfolio';
import { testimonialReducer } from '@app/modules/client-testimonial';
import { reviewsReducer } from '@app/modules/reviews';
import { dashboardReducer } from '@app/modules/dashboard';
import { managerDashboardReducer } from '@app/modules/manager-dashboard';
import { taskBoardReducer } from '@app/modules/task-board';
import { myTasksReducer } from '@app/modules/tasks';
import { overdueTasksReducer } from '@app/modules/overdueTasks';
import { etaReducer } from '@app/modules/eta';

/* -----[ persist configurations ]------ */
const rootPersistConfig = {
  key: 'root',
  storage: Storage,
  whitelist: ['version', 'commonReducer'],
};

// Preserver below slice even after logout
const preserveAlways = ['bootstrapReducer'];
const stateVersion = 1; // Update it whenever change state structure of whitelist slice

const appReducer = combineReducers({
  testimonialReducer,
  portfolioReducer,
  clientsReducer,
  bootstrapReducer,
  authReducer,
  departmentsReducer,
  designationsReducer,
  employeesReducer,
  roleReducer,
  myProfileReducer,
  commonReducer,
  assetsReducer,
  holidaysReducer,
  leaveManagementReducer,
  leavesReducer,
  candidateReducer,
  documentsReducer,
  IntervieweeReducer,
  internsReducer,
  projectReducer,
  reviewsReducer,
  dashboardReducer,
  managerDashboardReducer,
  taskBoardReducer,
  myTasksReducer,
  overdueTasksReducer,
  etaReducer,
  version: (state = stateVersion) => state,
  commonSlice: persistReducer(rootPersistConfig, commonSlice),
});

const rootReducer = (state: ReturnType<typeof appReducer>, action: Action<any>) => {
  if (action.type === logout.type) {
    const newState: any = Object.keys(state).reduce((acc, key) => {
      if (preserveAlways.includes(key)) {
        (acc as any)[key] = (state as any)[key];
      } else {
        (acc as any)[key] = undefined;
      }
      return acc;
    }, {});
    return appReducer(newState, action);
  }

  return appReducer(state, action);
};

const persistRootReducer = persistReducer(rootPersistConfig, (state, action) => {
  const migratedState = migrateState(state);
  return rootReducer(migratedState, action);
});

const migrateState = (state: any) => {
  if (state !== undefined && state.version !== stateVersion) {
    return {
      ...state,
      version: stateVersion,
    };
  }

  return state;
};

export type RootState = ReturnType<typeof rootReducer>;

export { persistRootReducer };
